import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Option 3: Minimalist/Flat Style
 * Clean, simple, no effects
 */
const MinimalProgress = ({ currentStep = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const steps = [
    { id: 1, name: 'Business Context' },
    { id: 2, name: 'Table Selection' },
    { id: 3, name: 'Domain Classification' },
    { id: 4, name: 'Data Profiling' },
    { id: 5, name: 'Relationship Identification' },
    { id: 6, name: 'Business Glossary' },
  ];

  const currentStepInfo = steps[currentStep - 1];
  const progressPercentage = ((currentStep - 1) / 5) * 100;

  return (
    <div
      className="absolute top-4 left-4 z-50 transition-all duration-200"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      {/* Compact View */}
      {!isExpanded && (
        <div className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2">
          <div className="flex items-center gap-2.5">
            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></div>
            <div className="flex flex-col">
              <span className="text-[10px] text-blue-400 font-semibold">
                {currentStep}/{steps.length}
              </span>
              <span className="text-[11px] text-gray-400">
                {currentStepInfo?.name}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Expanded View */}
      {isExpanded && (
        <div className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 min-w-[280px]">
          {/* Header */}
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-700">
            <span className="text-xs text-gray-300 font-medium">Progress</span>
            <span className="text-xs text-blue-400 font-semibold">
              {currentStep}/{steps.length}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="mb-3">
            <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all duration-500"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>

          {/* Steps List */}
          <div className="space-y-2">
            {steps.map((step) => {
              const isComplete = step.id < currentStep;
              const isCurrent = step.id === currentStep;
              const isPending = step.id > currentStep;

              return (
                <div key={step.id} className="flex items-center gap-2.5 text-xs">
                  {isComplete && (
                    <>
                      <div className="w-4 h-4 rounded bg-emerald-500 flex items-center justify-center">
                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-gray-500 line-through">{step.name}</span>
                    </>
                  )}
                  {isCurrent && (
                    <>
                      <div className="w-4 h-4 rounded bg-blue-500 flex items-center justify-center">
                        <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                      </div>
                      <span className="text-white font-medium">{step.name}</span>
                    </>
                  )}
                  {isPending && (
                    <>
                      <div className="w-4 h-4 rounded bg-slate-700 flex items-center justify-center">
                        <div className="w-1.5 h-1.5 bg-slate-600 rounded-full" />
                      </div>
                      <span className="text-gray-500">{step.name}</span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MinimalProgress;